"use client";
import { useState, useCallback } from "react";

export interface GeneralSettings {
  companyName: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface NotificationSettings {
  emailOnApprovals: boolean;
  emailOnRejections: boolean;
  emailOnComments: boolean;
  emailDigest: "daily" | "weekly" | "never";
  slackNotifications: boolean;
  pushNotifications: boolean;
}

export interface ApprovalSettings {
  requireMultipleApprovals: boolean;
  autoApproveUnder: number;
  approvalTimeout: number;
  escalationEnabled: boolean;
  escalationLevel: "department" | "finance" | "executive";
}

export interface IntegrationSettings {
  slackConnected: boolean;
  slackWorkspace: string;
  quickbooksConnected: boolean;
  quickbooksRealm: string;
  apiKeysEnabled: boolean;
  webhooksEnabled: boolean;
}

export interface SettingsSection {
  id: string;
  label: string;
  icon: string;
  description: string;
}

export interface SettingsController {
  activeSection: string;
  sections: SettingsSection[];
  generalSettings: GeneralSettings;
  notificationSettings: NotificationSettings;
  approvalSettings: ApprovalSettings;
  integrationSettings: IntegrationSettings;
  dirtyFields: Set<string>;
  saving: boolean;
  error: string | null;
  success: string | null;
  setActiveSection: (sectionId: string) => void;
  updateGeneralSettings: (data: Partial<GeneralSettings>) => void;
  updateNotificationSettings: (data: Partial<NotificationSettings>) => void;
  updateApprovalSettings: (data: Partial<ApprovalSettings>) => void;
  updateIntegrationSettings: (data: Partial<IntegrationSettings>) => void;
  saveSection: (sectionId: string) => Promise<void>;
  resetSection: (sectionId: string) => void;
  clearMessages: () => void;
}

const sections: SettingsSection[] = [
  {
    id: "general",
    label: "General",
    icon: "⚙️",
    description: "Company name, contact info, and location",
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: "🔔",
    description: "Email and alert preferences",
  },
  {
    id: "approvals",
    label: "Approvals",
    icon: "✓",
    description: "Approval workflow and thresholds",
  },
  {
    id: "integrations",
    label: "Integrations",
    icon: "🔗",
    description: "Connected apps and services",
  },
];

const initialGeneralSettings: GeneralSettings = {
  companyName: "BlueCopa Inc.",
  email: "hello@bluecopa.com",
  phone: "+1 (555) 123-4567",
  website: "www.bluecopa.com",
  address: "123 Business Ave",
  city: "San Francisco",
  state: "CA",
  zipCode: "94105",
  country: "United States",
};

const initialNotificationSettings: NotificationSettings = {
  emailOnApprovals: true,
  emailOnRejections: true,
  emailOnComments: false,
  emailDigest: "daily",
  slackNotifications: true,
  pushNotifications: true,
};

const initialApprovalSettings: ApprovalSettings = {
  requireMultipleApprovals: false,
  autoApproveUnder: 5000,
  approvalTimeout: 48,
  escalationEnabled: true,
  escalationLevel: "finance",
};

const initialIntegrationSettings: IntegrationSettings = {
  slackConnected: true,
  slackWorkspace: "bluecopa-workspace",
  quickbooksConnected: false,
  quickbooksRealm: "",
  apiKeysEnabled: true,
  webhooksEnabled: true,
};

export function useSettingsController(): SettingsController {
  const [activeSection, setActiveSection] = useState("general");
  const [generalSettings, setGeneralSettings] = useState(initialGeneralSettings);
  const [notificationSettings, setNotificationSettings] = useState(initialNotificationSettings);
  const [approvalSettings, setApprovalSettings] = useState(initialApprovalSettings);
  const [integrationSettings, setIntegrationSettings] = useState(initialIntegrationSettings);
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const markDirty = useCallback((fieldPath: string) => {
    setDirtyFields((prev) => new Set([...prev, fieldPath]));
  }, []);

  const updateGeneralSettings = useCallback((data: Partial<GeneralSettings>) => {
    setGeneralSettings((prev) => ({ ...prev, ...data }));
    Object.keys(data).forEach((key) => markDirty(`general.${key}`));
    setError(null);
  }, [markDirty]);

  const updateNotificationSettings = useCallback((data: Partial<NotificationSettings>) => {
    setNotificationSettings((prev) => ({ ...prev, ...data }));
    Object.keys(data).forEach((key) => markDirty(`notifications.${key}`));
    setError(null);
  }, [markDirty]);

  const updateApprovalSettings = useCallback((data: Partial<ApprovalSettings>) => {
    setApprovalSettings((prev) => ({ ...prev, ...data }));
    Object.keys(data).forEach((key) => markDirty(`approvals.${key}`));
    setError(null);
  }, [markDirty]);

  const updateIntegrationSettings = useCallback((data: Partial<IntegrationSettings>) => {
    setIntegrationSettings((prev) => ({ ...prev, ...data }));
    Object.keys(data).forEach((key) => markDirty(`integrations.${key}`));
    setError(null);
  }, [markDirty]);

  const saveSection = useCallback(
    async (sectionId: string) => {
      setSaving(true);
      setError(null);
      setSuccess(null);

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Filter out dirty fields for the current section and mark as clean
        const sectionPrefix = `${sectionId}.`;
        setDirtyFields((prev) => {
          const updated = new Set(prev);
          Array.from(updated).forEach((field) => {
            if (field.startsWith(sectionPrefix)) {
              updated.delete(field);
            }
          });
          return updated;
        });

        setSuccess(`${sectionId.charAt(0).toUpperCase() + sectionId.slice(1)} settings saved successfully!`);
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(`Failed to save ${sectionId} settings. Please try again.`);
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const resetSection = useCallback((sectionId: string) => {
    const sectionPrefix = `${sectionId}.`;
    setDirtyFields((prev) => {
      const updated = new Set(prev);
      Array.from(updated).forEach((field) => {
        if (field.startsWith(sectionPrefix)) {
          updated.delete(field);
        }
      });
      return updated;
    });

    switch (sectionId) {
      case "general":
        setGeneralSettings(initialGeneralSettings);
        break;
      case "notifications":
        setNotificationSettings(initialNotificationSettings);
        break;
      case "approvals":
        setApprovalSettings(initialApprovalSettings);
        break;
      case "integrations":
        setIntegrationSettings(initialIntegrationSettings);
        break;
      default:
        break;
    }
    setError(null);
  }, []);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return {
    activeSection,
    sections,
    generalSettings,
    notificationSettings,
    approvalSettings,
    integrationSettings,
    dirtyFields,
    saving,
    error,
    success,
    setActiveSection,
    updateGeneralSettings,
    updateNotificationSettings,
    updateApprovalSettings,
    updateIntegrationSettings,
    saveSection,
    resetSection,
    clearMessages,
  };
}
